import {
  anafAlerts,
  anafAsOf,
  activeOrder,
  analyse,
  fmtNum,
  historyDiff,
  historyDiffShort,
  historyDiffText,
  numInput,
  offerExpired,
  parseNum,
  roDate,
  shortfall,
  shortfallText,
  todayIso,
  type Offer,
  type Rfq,
  type RfqBench,
  t,
} from '@procumi/shared';
import { useState } from 'react';
import { api, errMsg } from '../lib/api';
import type { Toast } from '../hooks/useToast';
import { Dialog } from './Dialog';

/**
 * Cu cine ai rămas. Până la ecranul ăsta, cererea se închidea fără să se știe
 * cine a luat comanda: scorecard-ul ghicea câștigătorul din prețuri, iar
 * șantierul nu avea de unde ști ce s-a cumpărat pentru el.
 *
 * Se poate alege oricare furnizor căruia i-a plecat cererea, nu doar cei cu
 * ofertă în aplicație — de multe ori se bate palma la telefon, iar o regulă
 * mai strictă ar face omul să inventeze o ofertă ca să treacă de ea.
 */
export function ChoiceDialog({
  rfq,
  bench = {},
  toast,
  onUpdated,
  onClose,
}: {
  rfq: Rfq;
  /** ce spune istoricul firmei, pe poziție; lipsește până se încarcă */
  bench?: RfqBench;
  toast: Toast;
  onUpdated: (r: Rfq) => void;
  onClose: () => void;
}) {
  const a = analyse(rfq);
  /** Totalul ofertei complete a unui furnizor, dacă are una. */
  const totalul = (supplierId: string): number | null =>
    a.full.find((c) => c.supplier.id === supplierId)?.total ?? null;
  /** Totalul pus în casetă, rotunjit la bani: cantitate × preț lasă zecimale de calculator. */
  const inCaseta = (t: number | null): string => (t == null ? '' : numInput(Math.round(t * 100) / 100));

  // Propunem cel mai ieftin, fiindcă de acolo pleacă discuția — dar e doar o
  // propunere: tot rostul ecranului e să poți alege altceva și să spui de ce.
  // Cu un transport necunoscut care ar putea schimba clasamentul, nu propunem nimic.
  const implicit =
    rfq.chosen?.supplier.id ??
    (a.bestTotal == null ? '' : (a.full.find((c) => c.total === a.bestTotal)?.supplier.id ?? ''));

  const [supplierId, setSupplierId] = useState(implicit);
  const [note, setNote] = useState(rfq.chosen?.note ?? '');
  const [total, setTotal] = useState(() => {
    return inCaseta(rfq.chosen?.total ?? totalul(implicit));
  });
  const [close, setClose] = useState(!rfq.closedAt && !rfq.canceledAt);
  // Cu o comandă emisă, valoarea e cea din document și nu se mai scrie aici.
  const comanda = activeOrder(rfq);
  const [busy, setBusy] = useState(false);

  /** La schimbarea furnizorului, valoarea sare pe totalul lui — dacă n-ai scris tu alta. */
  const alege = (id: string) => {
    const vechi = totalul(supplierId);
    const neatins = total === '' || (vechi != null && total === inCaseta(vechi));
    setSupplierId(id);
    if (neatins) {
      setTotal(inCaseta(totalul(id)));
    }
  };

  // Prețurile furnizorului ales față de ce ai plătit înainte: ultima privire înainte de decizie.
  const ofertaAleasa = rfq.sends.find((s) => s.supplier.id === supplierId)?.offer;
  const fataDeIstoric = ofertaAleasa ? historyDiff(rfq.items, ofertaAleasa, bench) : null;

  /** Oferta furnizorului acoperă mai puțin decât s-a cerut. */
  const partialOf = (id: string) => a.cols.some((c) => c.supplier.id === id && c.partial);

  // Furnizorul bifat, la ANAF: inactiv sau radiat se spune separat, în roșu, fiindcă
  // e un risc fiscal, nu o notă despre ofertă.
  const anaf = rfq.sends.find((s) => s.supplier.id === supplierId)?.supplier.anaf;
  const anafBad = anafAlerts(anaf).filter((a) => a.level === 'danger');

  /** Ce trebuie să știi despre oferta bifată: expirată, parțială, alt produs. */
  const warnings = (o: Offer): string[] => {
    const out: string[] = [];
    if (offerExpired(o, todayIso()) && o.validUntil) {
      out.push(t('Oferta a expirat pe {0}: confirmă prețurile cu furnizorul.', roDate(o.validUntil)));
    }
    for (const it of rfq.items) {
      const sh = shortfall(o, it);
      if (sh) out.push(`${it.desc}: ${shortfallText(sh, it.unit)}.`);
      const alt = o.alts?.[it.id];
      if (alt) out.push(t('{0}: propune alt produs — {1}.', it.desc, alt));
    }
    return out;
  };

  const valoare = total.trim() === '' ? null : parseNum(total);
  const valoareGresita = total.trim() !== '' && (valoare == null || valoare < 0);

  const salveaza = async () => {
    if (!supplierId) {
      toast(t('Alege furnizorul.'));
      return;
    }
    if (valoareGresita) {
      toast(t('Valoarea comenzii nu e un număr.'));
      return;
    }
    setBusy(true);
    try {
      onUpdated(await api.chooseSupplier(rfq.id, { supplierId, note: note.trim(), total: valoare, close }));
      toast(t('Furnizor ales'));
      onClose();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const renunta = async () => {
    setBusy(true);
    try {
      onUpdated(await api.clearChoice(rfq.id));
      toast(t('Alegerea a fost ștearsă'));
      onClose();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose}>
      <div className="dlg-h">
        <div>
          <h2>{rfq.chosen ? t('Schimbă furnizorul ales') : t('Cu cine ai rămas?')}</h2>
          <p className="hint">
            {t(
              'Se scrie în istoricul șantierului și în scorecard-ul furnizorilor. Dacă n-ai ales cel mai ieftin, motivul e partea folositoare peste un an.',
            )}
          </p>
        </div>
      </div>

      <div className="dlg-b pad">
        {rfq.chosen?.split?.length ? (
          <p className="read-warn" style={{ marginBottom: 'var(--space-4)' }}>
            {t(
              'Acum comanda e împărțită între {0}. Dacă salvezi un singur furnizor, renunți la împărțire.',
              rfq.chosen.split.map((p) => p.supplier.name).join(' și '),
            )}
          </p>
        ) : null}
        <div className="checks" style={{ marginBottom: 'var(--space-4)' }}>
          {rfq.sends.map((s) => {
            const tot = totalul(s.supplier.id);
            const celMaiIeftin = tot != null && a.bestTotal != null && tot === a.bestTotal;
            return (
              <label key={s.id}>
                <input
                  type="radio"
                  name="furnizor-ales"
                  checked={supplierId === s.supplier.id}
                  onChange={() => alege(s.supplier.id)}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  {s.supplier.name}
                  {tot == null && partialOf(s.supplier.id) ? (
                    <span className="muted"> {t('· ofertă parțială')}</span>
                  ) : tot == null ? (
                    <span className="muted"> {t('· fără ofertă completă')}</span>
                  ) : a.full.find((c) => c.supplier.id === s.supplier.id)?.costUnknown ? (
                    <span className="muted"> {t('· {0} lei + transport necunoscut', fmtNum(tot))}</span>
                  ) : (
                    <span className="muted"> {t('· {0} lei', fmtNum(tot))}</span>
                  )}
                </span>
                {celMaiIeftin ? <span className="pill ok">{t('cel mai ieftin')}</span> : null}
              </label>
            );
          })}
        </div>

        {anafBad.length ? (
          <p className="anaf-warn" role="alert" style={{ marginBottom: 'var(--space-3)' }}>
            {anafBad.map((a) => a.text).join(' ')} ({anafAsOf(anaf!)})
          </p>
        ) : null}
        {ofertaAleasa && warnings(ofertaAleasa).length ? (
          <div className="read-warn" style={{ marginBottom: 'var(--space-4)' }}>
            {t('Înainte să alegi:')}
            <ul className="checks-list">
              {warnings(ofertaAleasa).map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {fataDeIstoric ? (
          <p
            className={`hist-line ${historyDiffShort(fataDeIstoric).tone}`}
            style={{ marginBottom: 'var(--space-4)' }}
          >
            {historyDiffText(fataDeIstoric)}
          </p>
        ) : null}

        <div className="row">
          <label className="f">
            {t('Valoarea comenzii (lei fără TVA)')}
            <input
              className="inp"
              inputMode="decimal"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              placeholder={t('opțional')}
              disabled={!!comanda}
            />
            <span className="hint">
              {comanda
                ? t(
                    'Valoarea vine din comanda {0}. Ca s-o schimbi, anulează comanda și refă-o.',
                    comanda.number,
                  )
                : t('Se completează cu totalul ofertei, dar poți scrie ce ai comandat de fapt.')}
            </span>
          </label>
          <label className="f">
            {t('Motivul alegerii')}
            <input
              className="inp"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('livra în 3 zile')}
              maxLength={500}
            />
            <span className="hint">{t('Un rând ajunge. Contează mai ales când n-a fost cel ieftin.')}</span>
          </label>
        </div>

        {!rfq.closedAt ? (
          <label className="primary-pick" style={{ marginTop: 'var(--space-3)' }}>
            <input type="checkbox" checked={close} onChange={(e) => setClose(e.target.checked)} />
            {t('Închide și cererea')}
          </label>
        ) : null}
      </div>

      <div className="dlg-f">
        {rfq.chosen ? (
          <button className="btn" disabled={busy} onClick={() => void renunta()}>
            {t('Șterge alegerea')}
          </button>
        ) : null}
        <button className="btn" onClick={onClose}>
          {t('Anulează')}
        </button>
        <button className="btn primary" disabled={busy || !supplierId} onClick={() => void salveaza()}>
          {busy ? t('Se salvează…') : t('Salvează')}
        </button>
      </div>
    </Dialog>
  );
}
